import axios from "axios";
import axiosInstance from "./axios";
import { ResponseType } from "@/types/CommonTypes";
import { ConversationWithActorScenario } from "@/types/ConversationTypes";

export const conversationsApi = {
  getAllConversations: async (
    page?: number,
    limit?: number,
    searchTerm?: string,
    sortBy?: string,
    sortDirection?: "ascending" | "descending"
  ): Promise<ResponseType<ConversationWithActorScenario[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) {
        queryParams.append("page", page.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (sortBy) {
        // Map frontend column keys to backend field names
        const backendFieldMap: { [key: string]: string } = {
          "conversation.date_created": "date_created",
          "conversation": "duration",
          "scenario": "scenario_name",
          "actor": "actor_name",
          "conversation_id": "conversation_id",
          "active": "active",
        };
        const backendSortBy = backendFieldMap[sortBy] || sortBy;
        queryParams.append("sort_by", backendSortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC"
        );
      }

      const response = await axiosInstance.get(
        `/conversations?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMyConversations: async (
    page: number,
    limit: number,
    sortBy?: string,
    sortDirection?: string,
    signal?: AbortSignal
  ): Promise<ResponseType<ConversationWithActorScenario[]>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());
      if (sortBy) {
        // Map frontend column keys to backend field names
        const backendFieldMap: { [key: string]: string } = {
          "conversation.date_created": "date_created",
          "conversation": "duration",
          "scenario": "scenario_name",
          "actor": "actor_name",
          "conversation_id": "conversation_id",
          "active": "active",
        };
        const backendSortBy = backendFieldMap[sortBy] || sortBy;
        queryParams.append("sort_by", backendSortBy);
      }
      if (sortBy && sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC"
        );
      }
      const response = await axiosInstance.get(
        `/conversations/me?${queryParams.toString()}`,
        { signal }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },

  getConversationsByUserId: async (
    userId: number,
    page: number,
    limit: number,
    sortBy?: string,
    sortDirection?: string,
    signal?: AbortSignal
  ): Promise<ResponseType<ConversationWithActorScenario[]>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());
      if (sortBy) {
        // Map frontend column keys to backend field names
        const backendFieldMap: { [key: string]: string } = {
          "conversation.date_created": "date_created",
          "conversation": "duration",
          "scenario": "scenario_name",
          "actor": "actor_name",
          "conversation_id": "conversation_id",
          "active": "active",
        };
        const backendSortBy = backendFieldMap[sortBy] || sortBy;
        queryParams.append("sort_by", backendSortBy);
      }
      if (sortBy && sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC"
        );
      }
      const response = await axiosInstance.get(
        `/conversations/user/${userId}?${queryParams.toString()}`,
        { signal }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },

  /**
   * Unified method to get files (reports/transcripts) from the API
   * @param conversationId - The conversation ID
   * @param document - Type of document: "report" or "transcript"
   * @param format - Format: "pdf" or "json"
   * @returns Blob for PDF, JSON object for JSON format
   */
  getFile: async (
    conversationId: string,
    document: "report" | "transcript",
    format: "pdf" | "json" = "pdf"
  ): Promise<Blob | unknown> => {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        conversation_id: conversationId,
        document: document,
        format: format,
      });

      // Call proxy route instead of Talks API directly (token hidden on server)
      const response = await fetch(
        `/api/proxy/talks/get-file?${params.toString()}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          const documentName = document === "report" ? "report" : "transcript";
          const formatName = format === "pdf" ? "PDF" : "JSON";
          throw new Error(`${formatName} ${documentName} not found`);
        }
        // Try to get error message from response
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || errorData.message || "Failed to fetch file"
          );
        } else {
          // For non-JSON errors (like 404 with text/plain), read as text
          const errorText = await response.text().catch(() => "");
          throw new Error(errorText || "Failed to fetch file");
        }
      }

      // Handle JSON response
      if (format === "json") {
        const data = await response.json();
        return data;
      }

      // Handle PDF/blob response
      const blob = await response.blob();
      return blob;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Failed to fetch file");
    }
  },

  // Download transcript PDF by conversationId (backward compatibility)
  getTranscriptPdf: async (conversationId: string): Promise<Blob> => {
    return conversationsApi.getFile(
      conversationId,
      "transcript",
      "pdf"
    ) as Promise<Blob>;
  },

  // Download report PDF by conversationId (updated to use conversation_id instead of filename)
  getReportPdf: async (conversationId: string): Promise<Blob> => {
    return conversationsApi.getFile(
      conversationId,
      "report",
      "pdf"
    ) as Promise<Blob>;
  },

  // Delete conversation by ID
  deleteConversation: async (conversationId: number): Promise<void> => {
    try {
      await axiosInstance.delete(`/conversations/${conversationId}`);
    } catch (error) {
      throw error;
    }
  },
};
